/**
 * An action log for part loading.
 *
 * Every decision this feature makes is invisible from the outside: which part a click resolved to, how
 * long the editor was hidden, whether the anchor was found again and how far the page drifted while it
 * settled. Guessing at those from a performance profile does not work - the profile shows time, and
 * every bug here is about POSITION. So each action records what it did, with the numbers.
 *
 * Read it with `window.__largeDocument.log()`. Cheap enough to leave on: one array push per action, and
 * actions happen on a click or a save, never per keystroke and never per element.
 */
import { SECTION_CLASS_NAME } from '../tiptap/nodes/section/constants';

/** Console output as well as the buffer. Off by default - the buffer is what analysis reads. */
export const TRACE_TO_CONSOLE = false;

/** Asked for, not paid by everyone: each entry costs a forced layout. `__largeDocument.measure(true)`. */
let measuring = false;
export const setMeasuring = (on: boolean) => {
  measuring = on;
};

/** Bounded so a long session cannot grow it without limit. */
const MAX_ENTRIES = 2000;

export type TTraceEntry = { at: number; event: string } & Record<string, unknown>;

const entries: TTraceEntry[] = [];

/** Set by the controller so every entry can carry the scroll position without the caller passing it. */
let scrollerForTrace: HTMLElement | null = null;
export const setTraceScroller = (scroller: HTMLElement | null) => {
  scrollerForTrace = scroller;
};

export const trace = (event: string, data?: Record<string, unknown>) => {
  const entry: TTraceEntry = { at: Math.round(performance.now()), event, ...data };
  if (scrollerForTrace) entry.scrollTop = Math.round(scrollerForTrace.scrollTop);
  entries.push(entry);
  if (entries.length > MAX_ENTRIES) entries.splice(0, entries.length - MAX_ENTRIES);
  // A string, not the object: a saved console log truncates objects after a few fields, which loses
  // exactly the numbers these entries exist for.
  if (TRACE_TO_CONSOLE) console.debug('[largeDocument]', event, JSON.stringify(data ?? {}));
};

export const traceLog = (): readonly TTraceEntry[] => entries;

// The editor renders the title as a tag, the static half as a class.
const TITLE_SELECTOR = 'title-node, .title-node';

/** 0 nowhere, 1 the static half above, 2 the editor, 3 the static half below. */
const titleContainer = (): number => {
  const title = document.querySelector(TITLE_SELECTOR);
  if (!title) return 0;
  if (title.closest('.ProseMirror')) return 2;
  const block = title.closest<HTMLElement>('[data-large-document-block]')?.dataset.largeDocumentBlock;
  return block === 'above' ? 1 : block === 'below' ? 3 : 0;
};

// The piece decides whether a section draws the page's top margin, so two copies that disagree move.
const sectionPieces = (): string =>
  [...document.querySelectorAll<HTMLElement>(`.${SECTION_CLASS_NAME}`)]
    .map((section) => {
      const where = section.closest('.ProseMirror')
        ? 'editor'
        : (section.closest<HTMLElement>('[data-large-document-block]')?.dataset.largeDocumentBlock ?? '?');
      return `${where}:${section.getAttribute('data-section-piece') ?? 'whole'}`;
    })
    .join(' ');

/**
 * The page's shape in one line: the three boxes that make up the document, and its total height.
 * Every step that can move the page records this, so a shift can be attributed to the step that did it.
 */
export const geometry = (): Record<string, number | string> => {
  if (!measuring) return {};
  const halves = document.querySelectorAll<HTMLElement>('.large-document-static');
  const editor = document.querySelector<HTMLElement>('.ProseMirror');
  const scroller = (() => {
    let el = editor?.parentElement ?? null;
    while (el) {
      const style = getComputedStyle(el);
      if ((style.overflowY === 'auto' || style.overflowY === 'scroll') && el.scrollHeight > el.clientHeight + 1)
        return el;
      el = el.parentElement;
    }
    return null;
  })();
  const height = (el: Element | null | undefined) => (el ? Math.round(el.getBoundingClientRect().height) : -1);
  return {
    docH: scroller?.scrollHeight ?? -1,
    scrollTop: Math.round(scroller?.scrollTop ?? -1),
    aboveH: height(halves[0]),
    editorH: height(editor),
    editorBoxH: height(editor?.parentElement),
    belowH: height(halves[1]),
    aboveKids: halves[0]?.children.length ?? -1,
    editorKids: editor?.children.length ?? -1,
    belowKids: halves[1]?.children.length ?? -1,
    // The title changes container on a swap to the first part, so its own box is worth its own field.
    titleH: height(document.querySelector(TITLE_SELECTOR)),
    titleIn: titleContainer(),
    pieces: sectionPieces(),
  };
};

export const clearTrace = () => {
  entries.length = 0;
};

/** Which cell a point is over, and how that row is laid out. The same shape for both copies. */
export const cellUnder = (x: number, y: number): Record<string, unknown> => {
  const under = document.elementFromPoint(x, y);
  const cell = under?.closest('td,th') ?? null;
  const row = cell?.parentElement ?? null;
  const table = cell?.closest('table') ?? null;
  return {
    cell: cell && row ? [...row.children].indexOf(cell) : null,
    text: (cell?.textContent ?? '').trim().slice(0, 24) || null,
    tableId: (table?.getAttribute('data-id') ?? '').slice(0, 8) || null,
    tableWidth: table ? Math.round(table.getBoundingClientRect().width) : null,
    rowWidths: row ? [...row.children].map((one) => Math.round(one.getBoundingClientRect().width)) : null,
  };
};
